/** Termin im Laden (MP-C): Einzug/Auszug/Sonstiges, Tag und freies 30-Minuten-Fenster aus den Öffnungszeiten. */
import { useTx } from "../i18n";
import { useState } from "react";
import { Link } from "react-router";
import { Button, Notice, Seitenzustand, TextField } from "../ui";
import { Card } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatTag, tagVon } from "@shared/zeit";
import { useTerminAbsagen, useTerminBuchen, useTermine } from "./portal-c-api";

const WT = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const ART_TEXT = { einzug: "Einräumen", auszug: "Abholen", sonstiges: "Sonstiges" } as const;

export function Termin() {
  const tx = useTx();
  const q = useTermine();
  const buchen = useTerminBuchen();
  const absagen = useTerminAbsagen();
  const [art, setArt] = useState<"einzug" | "auszug" | "sonstiges">("einzug");
  const [tag, setTag] = useState<string | null>(null);
  const [von, setVon] = useState<string | null>(null);
  const [notiz, setNotiz] = useState("");
  const [meldung, setMeldung] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const heute = tagVon();

  return (
    <Seitenzustand query={q} lines={5}>
      {(d) => {
        const gewaehlt = d.fenster.find((t) => t.tag === (tag ?? d.fenster[0]?.tag));
        const geplant = d.termine.filter((t) => t.status === "geplant" && t.tag >= heute);
        return (
          <div className="sc-col" style={{ gap: "var(--space-4)" }}>
            <div className="sc-row">
              <Link to="/mieter/regal" className="rg-btn rg-btn--quiet" aria-label="Zurück">‹</Link>
              <h1 className="title">{tx("Termin im Laden")}</h1>
            </div>
            {meldung ? <Notice tone={meldung.tone}>{meldung.text}</Notice> : null}
            {geplant.length ? (
              <Card title={tx("Deine Termine")}>
                {geplant.map((t) => (
                  <p key={t.id} className="sc-spread" style={{ margin: "4px 0" }}>
                    <span>{ART_TEXT[t.art]} · {formatTag(t.tag)}, {t.von}–{t.bis} Uhr{t.notiz ? <span className="sc-muted sc-small"> · {t.notiz}</span> : null}</span>
                    <Button variant="quiet" icon="x" loading={absagen.isPending} onClick={() => confirm("Termin absagen?") && absagen.mutate(t.id)}>Absagen</Button>
                  </p>
                ))}
              </Card>
            ) : null}
            <div className="sc-toolbar" role="radiogroup" aria-label="Art">
              {(["einzug", "auszug", "sonstiges"] as const).map((a) => <button key={a} type="button" className="sc-pill" aria-pressed={art === a} onClick={() => setArt(a)}>{ART_TEXT[a]}</button>)}
            </div>
            <h2 className="heading">{tx("Tag wählen")}</h2>
            <div className="sc-toolbar" style={{ overflowX: "auto", flexWrap: "nowrap" }}>
              {d.fenster.map((t) => (
                <button key={t.tag} type="button" className="sc-pill" style={{ flex: "none" }} aria-pressed={(tag ?? d.fenster[0]?.tag) === t.tag} onClick={() => { setTag(t.tag); setVon(null); }}>
                  {WT[new Date(`${t.tag}T12:00:00Z`).getUTCDay()]} {formatTag(t.tag).slice(0, 6)}
                </button>
              ))}
            </div>
            {d.fenster.length === 0 ? <Notice tone="info">Der Laden hat keine Öffnungszeiten hinterlegt – bitte direkt im Laden fragen.</Notice> : null}
            {gewaehlt ? (
              <>
                <h2 className="heading">{tx("Uhrzeit wählen")}</h2>
                <div className="mp-kacheln" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
                  {gewaehlt.fenster.map((f) => (
                    <button key={f.von} type="button" className={`sc-pill${von === f.von ? "" : ""}`} aria-pressed={von === f.von} disabled={!f.frei} onClick={() => setVon(f.von)} style={{ justifyContent: "center", opacity: f.frei ? 1 : 0.45 }}>
                      {f.von}–{f.bis}{f.frei ? "" : " belegt"}
                    </button>
                  ))}
                </div>
              </>
            ) : null}
            <TextField label="Hinweis für den Laden (freiwillig)" value={notiz} onChange={(e) => setNotiz(e.target.value)} maxLength={200} placeholder="z. B. Komme mit zwei Kisten" />
            {art === "einzug" ? <Notice tone="info" title={tx("Etiketten vorher drucken")}>Kleb die Etiketten schon zu Hause auf – dann geht das Einräumen schnell.</Notice> : null}
            <div className="mp-foot">
              <Button variant="primary" size="lg" block icon="calendar-days" loading={buchen.isPending} disabled={!gewaehlt || !von} onClick={() => gewaehlt && von && buchen.mutate({ art, tag: gewaehlt.tag, von, notiz: notiz.trim() }, { onSuccess: () => { setVon(null); setNotiz(""); setMeldung({ tone: "success", text: `Termin bestätigt: ${formatTag(gewaehlt.tag)}, ${von} Uhr. Wir erinnern dich am Vortag.` }); }, onError: (e) => setMeldung({ tone: "error", text: e instanceof ApiFehler ? e.message : "Das hat nicht geklappt." }) })}>
                Termin bestätigen
              </Button>
            </div>
          </div>
        );
      }}
    </Seitenzustand>
  );
}
